<template>
  <sg-tooltip
    ref="refPopper"
    v-model:visible="pickerVisible"
    theme="light"
    pure
    trigger="click"
    v-bind="$attrs"
    transfer
    transition="sg-zoom-in-top"
    :drop-class-name="`sg-picker__popper`"
    :popper-options="popperOptions"
    :placement="(placement as any)"
    :fallback-placements="['bottom', 'top', 'right', 'left']"
    :gpu-acceleration="false"
    :stop-popper-mouse-event="false"
    :hide-after="0"
    persistent
    @before-show="onBeforeShow"
    @hide="onHide"
  >
    <template #default>
      <sg-input
        v-if="!isRangeInput"

        ref="inputRef"
        :model-value="(displayValue as any)"
        v-bind="attrs"
        :size="pickerSize"
        :disabled="pickerDisabled"
        :placeholder="`${type === 'time' || type === 'timerange' ? '请选择时间' : '请选择日期'}`"
        class="sg-date-editor"
        :class="['sg-date-editor--' + type, $attrs.class || undefined]"
        :style="$attrs.style || undefined"
        :readonly="!editable || readonly || isDatesPicker || type === 'week'"
        :validate-event="false"
        @on-input="onUserInput"
        @on-focus="handleFocusInput"
        @on-blur="handleBlurInput"
        @keydown="handleKeydown"
        @on-change="handleChange"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
        @click.stop
      >
        <template #prefix>
          <sg-icon
            v-if="triggerIcon"
            class="sg-input__icon prefix-icon"
            @mousedown.prevent="onMouseDownInput"
            @touchstart="onTouchStartInput"
          >
            <component :is="triggerIcon"></component>
          </sg-icon>
        </template>
        <template #suffix>
          <sg-icon
            v-if="showClose && clearIcon"
            class="sg-input__icon clear-icon"
            @click.stop="onClearIconClick"
          >
            <component :is="clearIcon" />
          </sg-icon>
        </template>
      </sg-input>
      <div
        v-else
        ref="inputRef"
        class="sg-date-editor sg-range-editor sg-input__inner"
        :class="[
          'sg-date-editor--' + type,
          pickerSize ? `sg-range-editor--${pickerSize}` : '',
          pickerDisabled ? 'is-disabled' : '',
          pickerVisible ? 'is-active' : '',
          $attrs.class || undefined,
        ]"
        :style="($attrs.style as any )"
        @click="handleFocusInput"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
        @keydown="handleKeydown"
      >
        <sg-icon
          v-if="triggerIcon"
          class="sg-input__icon sg-range__icon prefix-icon"
          @mousedown.prevent="onMouseDownInput"
          @touchstart="onTouchStartInput"
        >
          <component :is="triggerIcon"></component>
        </sg-icon>
        <input
          autocomplete="off"
          :placeholder="`${type === 'time' || type === 'timerange' ? '请选择时间' : '请选择日期'}`"
          :value="displayValue && displayValue[0]"
          :disabled="pickerDisabled"
          :readonly="!editable || readonly"
          class="sg-range-input"
          @input="handleStartInput"
          @change="handleStartChange"
          @focus="handleFocusInput"
          @blur="handleBlurInput"
        />
        <slot name="range-separator">
          <span class="sg-range-separator">{{ rangeSeparator }}</span>
        </slot>
        <input
          v-bind="attrs"
          autocomplete="off"
          :placeholder="`${type === 'time' || type === 'timerange' ? '请选择时间' : '请选择日期'}`"
          :value="displayValue && displayValue[1]"
          :disabled="pickerDisabled"
          :readonly="!editable || readonly"
          class="sg-range-input"
          @focus="handleFocusInput"
          @blur="handleBlurInput"
          @input="handleEndInput"
          @change="handleEndChange"
        />
        <sg-icon
          v-if="clearIcon"
          class="sg-input__icon sg-range__close-icon"
          :class="{
            'sg-range__close-icon--hidden': !showClose,
          }"
          @click="onClearIconClick"
        >
          <component :is="clearIcon" />
        </sg-icon>
      </div>
    </template>
    <template #content>
      <slot
        :confirm="confirm"
        :visible="pickerVisible"
        :actual-visible="pickerActualVisible"
        :parsed-value="parsedValue"
        :format="format"
        :unlink-panels="unlinkPanels"
        :type="type"
        :default-value="defaultValue"
        @pick="onPick"
        @select-range="setSelectionRange"
        @set-picker-option="onSetPickerOption"
        @on-calendar-change="onCalendarChange"
        @on-panel-change="onPanelChange"
        @mousedown.stop
      ></slot>
    </template>
  </sg-tooltip>
</template>
<script lang="ts">
import {
  defineComponent,
  ref,
  computed,
  nextTick,
  inject,
  watch,
  provide,
  unref,
} from 'vue'
import dayjs from 'dayjs'
import isEqual from 'lodash/isEqual'
import { onClickOutside } from '@vueuse/core'
import { useLocale, useSize, useFormItem } from '@sgui-plus/hooks'
import SgInput from '@sgui-plus/components/input'
import SgIcon from '@sgui-plus/components/icon'
import SgTooltip from '@sgui-plus/components/tooltip'
import { EVENT_CODE } from '@sgui-plus/utils/aria'
import { isEmpty } from '@sgui-plus/utils/util'
import { debugWarn } from '@sgui-plus/utils/error'
import { Time, Date as IconDate } from '@sgui-plus/icons-vue'
import { timePickerDefaultProps } from './props'
import { TimePickerContextKey, PopperOptionsContextKey } from './constant'

import type { Dayjs } from 'dayjs'
import type { ComponentPublicInstance } from 'vue'
import type { Options } from '@popperjs/core'

interface PickerOptions {
  isValidValue: (date: Dayjs) => boolean
  handleKeydown: (event: KeyboardEvent) => void
  parseUserInput: (value: Dayjs) => dayjs.Dayjs
  formatToString: (value: Dayjs) => string | string[]
  getRangeAvailableTime: (date: Dayjs) => dayjs.Dayjs
  getDefaultValue: () => Dayjs
  panelReady: boolean
  handleClear: () => void
  handleFocusPicker?: () => void
}

// Date object and string
const dateEquals = function (a: Date | any, b: Date | any) {
  const aIsDate = a instanceof Date
  const bIsDate = b instanceof Date
  if (aIsDate && bIsDate) {
    return a.getTime() === b.getTime()
  }
  if (!aIsDate && !bIsDate) {
    return a === b
  }
  return false
}

const valueEquals = function (a: Array<Date> | any, b: Array<Date> | any) {
  const aIsArray = a instanceof Array
  const bIsArray = b instanceof Array
  if (aIsArray && bIsArray) {
    if (a.length !== b.length) {
      return false
    }
    return (a as Array<Date>).every((item, index) => dateEquals(item, b[index]))
  }
  if (!aIsArray && !bIsArray) {
    return dateEquals(a, b)
  }
  return false
}

const parser = function (
  date: Date | string,
  format: string,
  lang: string
): Dayjs | undefined {
  const day =
    isEmpty(format) || format === 'x'
      ? dayjs(date).locale(lang)
      : dayjs(date, format).locale(lang)
  return day.isValid() ? day : undefined
}

const formatter = function (date: number | Date, format: string, lang: string) {
  if (isEmpty(format)) return date
  if (format === 'x') return +date
  return dayjs(date).locale(lang).format(format)
}

export default defineComponent({
  name: 'Picker',
  components: {
    SgInput,
    SgTooltip,
    SgIcon,
  },
  props: timePickerDefaultProps,
  emits: [
    'update:modelValue',
    'on-change',
    'on-open-change',
    'on-focus',
    'on-blur',
    'on-calendar-change',
    'on-panel-change',
    'on-visible-change',
    'on-clear'
  ],
  setup(props, ctx) {
    const { lang } = useLocale()
    const { form, formItem } = useFormItem()
    const attrs = ctx.attrs
    const popperOptions = inject(PopperOptionsContextKey, {} as Options)
    const refPopper = ref<InstanceType<typeof SgTooltip>>()
    const inputRef = ref<HTMLElement | ComponentPublicInstance>()
    const pickerVisible = ref(false)
    const pickerActualVisible = ref(false)
    const valueOnOpen = ref<null | string | number | Date | (string | number | Date)[]>(null)

    let hasJustTabExitedInput = false
    let ignoreFocusEvent = false

    watch(pickerVisible, (val) => {
      if (!val) {
        userInput.value = null
        nextTick(() => {
          emitChange(props.modelValue)
        })
        ctx.emit('on-open-change', val)
        // blurInput()
      } else {
        ctx.emit('on-open-change', val)
        nextTick(() => {
          if (val) {
            valueOnOpen.value = props.modelValue
          }
        })
      }
    })
    const emitChange = (val, isClear?: boolean) => {
      // determine user real change only
      if (isClear || !valueEquals(val, valueOnOpen.value)) {
        ctx.emit('on-change', val)
        props.validateEvent && formItem?.validate('change').catch((err) => debugWarn(err))
      }
    }
    const emitInput = (val) => {
      if (!valueEquals(props.modelValue, val)) {
        let formatValue
        if (Array.isArray(val)) {
          if(props.type === 'datenoonrange') {
            formatValue = [
              {
                date: formatter(val[0].date, props.valueFormat as string, lang.value),
                noon: val[0].noon.value
              },
              {
                date: formatter(val[1].date, props.valueFormat as string, lang.value),
                noon: val[1].noon.value
              }
            ]
          } else {
              formatValue = val.map((_) =>
              formatter(_, props.valueFormat as string, lang.value)
            )
          }
        } else if (val) {
          formatValue = formatter(val, props.valueFormat as string, lang.value)
        }
        ctx.emit('update:modelValue', val ? formatValue : val, lang.value)
      }
    }

    const refInput = computed<HTMLInputElement[]>(() => {
      if (inputRef.value) {
        const _r = isRangeInput.value
          ? inputRef.value
          : (inputRef.value as any as ComponentPublicInstance).$el
        return Array.from<HTMLInputElement>(_r.querySelectorAll('input'))
      }
      return []
    })

    const setSelectionRange = (start, end, pos) => {
      const _inputs = refInput.value
      if (!_inputs.length) return
      if (!pos || pos === 'min') {
        _inputs[0].setSelectionRange(start, end)
        _inputs[0].focus()
      } else if (pos === 'max') {
        _inputs[1].setSelectionRange(start, end)
        _inputs[1].focus()
      }
    }

    const focusOnInputBox = () => {
      focus(true, true)
      nextTick(() => {
        ignoreFocusEvent = false
      })
    }

    const onPick = (date: any = '', visible = false) => {
      if (!visible) {
        ignoreFocusEvent = true
      }
      pickerVisible.value = visible
      let result
      if (Array.isArray(date)) {
        if(props.type === 'datenoonrange') {
          result = [
            {
              date: date[0].date.toDate(),
              noon: date[0].noon
            },
            {
              date: date[1].date.toDate(),
              noon: date[1].noon
            }
          ]
        } else{
          result = date.map((_) => _.toDate())
        }
      }else {
        // clear btn emit null
        result = date ? date.toDate() : date
      }
      userInput.value = null
      emitInput(result)
    }

    const onBeforeShow = () => {
      pickerActualVisible.value = true
    }

    const onHide = () => {
      pickerActualVisible.value = false
      pickerVisible.value = false
      ignoreFocusEvent = false
    }

    const focus = (focusStartInput = true, isIgnoreFocusEvent = false) => {
      ignoreFocusEvent = isIgnoreFocusEvent
      const [leftInput, rightInput] = unref(refInput)
      let input = leftInput
      if (!focusStartInput && isRangeInput.value) {
        input = rightInput
      }
      if (input) {
        input.focus()
      }
    }

    const handleFocusInput = (e) => {
      if (props.readonly || pickerDisabled.value || pickerVisible.value || ignoreFocusEvent) return
      pickerVisible.value = true
      if (props.clearable) {
        showClose.value = true
      }
      ctx.emit('on-focus', e)
    }

    // const handleBlur = () => {
    //   refPopper.value?.onClose()
    //   blurInput()
    // }

    let currentHandleBlurDeferCallback:
      | (() => Promise<void> | undefined)
      | undefined = undefined

    const handleBlurInput = (e?: FocusEvent) => {
      const handleBlurDefer = async () => {
        setTimeout(() => {
          if (currentHandleBlurDeferCallback === handleBlurDefer) {
            if (
              !(
                refPopper.value?.isFocusInsideContent() && !hasJustTabExitedInput
              ) &&
              refInput.value.filter((input) => {
                return input.contains(document.activeElement)
              }).length === 0
            ) {
              handleChange()
              // pickerVisible.value = false
              ctx.emit('on-blur', e)
              props.validateEvent && formItem?.validate('blur').catch((err) => debugWarn(err))
            }
            hasJustTabExitedInput = false
          }
        }, 0)
      }
      currentHandleBlurDeferCallback = handleBlurDefer
      handleBlurDefer()
    }

    const pickerDisabled = computed(() => {
      return props.disabled || form?.disabled
    })

    const parsedValue = computed(() => {
      let result
      if (valueIsEmpty.value) {
        if (pickerOptions.value.getDefaultValue) {
          result = pickerOptions.value.getDefaultValue()
        }
      } else {
        if (Array.isArray(props.modelValue)) {
          if(props.type === 'datenoonrange' ) {
            result = [
              {
                date: parser(props.modelValue[0].date as any  , props.valueFormat as string, lang.value),
                noon: props.modelValue[0].noon
              },
              {
                date: parser(props.modelValue[1].date as any , props.valueFormat as string, lang.value),
                noon: props.modelValue[1].noon
              }
            ]
          } else {
            result = props.modelValue.map((_) =>
              parser(_ as any , props.valueFormat as string, lang.value)
            )
          }
        } else {
          result = parser(props.modelValue as Date | string, props.valueFormat as string, lang.value)
        }
      }

      if (pickerOptions.value.getRangeAvailableTime) {
        const availableResult =
          pickerOptions.value.getRangeAvailableTime(result)
        if (!isEqual(availableResult, result)) {
          result = availableResult
          emitInput(
            Array.isArray(result)
              ? result.map((_) => _.toDate())
              : result.toDate()
          )
        }
      }
      if (Array.isArray(result) && result.some((_) => !_)) {
        result = []
      }
      return result
    })

    const displayValue = computed(() => {
      if (!pickerOptions.value.panelReady) return
      let formattedValue
      if(props.type === 'datenoonrange' && parsedValue.value) {
        formattedValue = [`${formatDayjsToString(parsedValue.value[0].date)} ${parsedValue.value[0].noon === 'am' ? '上午' : '下午'}`, `${formatDayjsToString(parsedValue.value[1].date)} ${parsedValue.value[1].noon === 'am' ? '上午' : '下午'}`]
      }else {
        formattedValue = formatDayjsToString(parsedValue.value)
      }
      if (Array.isArray(userInput.value)) {
        return [
          userInput.value[0] || (formattedValue && formattedValue[0]) || '',
          userInput.value[1] || (formattedValue && formattedValue[1]) || '',
        ]
      } else if (userInput.value !== null) {
        return userInput.value
      }
      if (!isTimePicker.value && valueIsEmpty.value) return
      if (!pickerVisible.value && valueIsEmpty.value) return
      if (formattedValue) {
        return isDatesPicker.value
          ? (formattedValue as Array<string>).join(', ')
          : formattedValue
      }
      return ''
    })

    const isTimeLikePicker = computed(() => props.type.includes('time'))

    const isTimePicker = computed(() => props.type.startsWith('time'))

    const isDatesPicker = computed(() => props.type === 'dates')

    const triggerIcon = computed(
      () => isTimeLikePicker.value ? Time : IconDate
    )

    const showClose = ref(false)

    const onClearIconClick = (event) => {
      if (props.readonly || pickerDisabled.value) return
      if (showClose.value) {
        event.stopPropagation()
        focusOnInputBox()
        emitInput(null)
        emitChange(null, true)
        ctx.emit('on-clear')
        showClose.value = false
        pickerVisible.value = false
        pickerOptions.value.handleClear && pickerOptions.value.handleClear()
      }
    }
    const valueIsEmpty = computed(() => {
      return (
        !props.modelValue ||
        (Array.isArray(props.modelValue) && !props.modelValue.length)
      )
    })

    const onMouseDownInput = async (event: MouseEvent) => {
      if (props.readonly || pickerDisabled.value) return
      if (
        (event.target as HTMLElement)?.tagName !== 'INPUT' ||
        refInput.value.includes(document.activeElement as HTMLInputElement)
      ) {
        pickerVisible.value = true
      }
    }

    const onMouseEnter = () => {
      if (props.readonly || pickerDisabled.value) return
      if (!valueIsEmpty.value && props.clearable) {
        showClose.value = true
      }
    }

    const onMouseLeave = () => {
      showClose.value = false
    }

    const onTouchStartInput = (event: TouchEvent) => {
      if (props.readonly || pickerDisabled.value) return
      if (
        (event.touches[0].target as HTMLElement)?.tagName !== 'INPUT' ||
        refInput.value.includes(document.activeElement as HTMLInputElement)
      ) {
        pickerVisible.value = true
      }
    }

    const isRangeInput = computed(() => {
      return props.type.indexOf('range') > -1
    })

    const pickerSize = useSize()

    const popperPaneRef = computed(() => {
      return refPopper.value?.popperRef?.contentRef
    })

    const popperEl = computed(() => unref(refPopper)?.popperRef?.contentRef)
    const actualInputRef = computed(() => {
      if (unref(isRangeInput)) {
        return unref(inputRef)
      }

      return (unref(inputRef) as ComponentPublicInstance)?.$el
    })

    onClickOutside(actualInputRef as any, (e: PointerEvent) => {
      const unrefedPopperEl = unref(popperEl)
      const inputEl = unref(actualInputRef)
      if (
        (unrefedPopperEl &&
          (e.target === unrefedPopperEl ||
            e.composedPath().includes(unrefedPopperEl))) ||
        e.target === inputEl ||
        e.composedPath().includes(inputEl) || props.type === "datenoonrange"
      )
        return
      pickerVisible.value = false
    })

    const userInput = ref<null | any[] | string>(null)

    const handleChange = () => {
      if (userInput.value) {
        const value = parseUserInputToDayjs(displayValue.value)
        if (value) {
          if (isValidValue(value)) {
            emitInput(
              Array.isArray(value)
                ? value.map((_) => _.toDate())
                : value.toDate()
            )
            userInput.value = null
          }
        }
      }
      if (userInput.value === '') {
        emitInput(null)
        emitChange(null)
        userInput.value = null
      }
    }

    // const blurInput = () => {
    //   refInput.value.forEach((input) => input.blur())
    // }

    const parseUserInputToDayjs = (value) => {
      if (!value) return null
      return pickerOptions.value.parseUserInput!(value)
    }

    const formatDayjsToString = (value) => {
      if (!value) return null
      return pickerOptions.value.formatToString!(value)
    }

    const isValidValue = (value) => {
      return pickerOptions.value.isValidValue!(value)
    }

    const handleKeydown = async (event) => {
      if (props.readonly || pickerDisabled.value) return

      const code = event.code
      if (code === EVENT_CODE.esc) {
        pickerVisible.value = false
        event.preventDefault()
        event.stopPropagation()
        return
      }

      if (code === EVENT_CODE.down) {
        if (pickerOptions.value.handleFocusPicker) {
          event.preventDefault()
          event.stopPropagation()
        }
        if (pickerVisible.value === false) {
          pickerVisible.value = true
          await nextTick()
        }
        if (pickerOptions.value.handleFocusPicker) {
          pickerOptions.value.handleFocusPicker()
          return
        }
      }

      if (code === EVENT_CODE.tab) {
        // if (!isRangeInput.value) {
        //   handleChange()
        //   pickerVisible.value = false
        //   event.stopPropagation()
        // } else {
        //   // user may change focus between two input
        //   setTimeout(() => {
        //     if (refInput.value.indexOf(document.activeElement as any) === -1) {
        //       pickerVisible.value = false
        //       blurInput()
        //     }
        //   }, 0)
        // }
        hasJustTabExitedInput = true
        return
      }

      if (code === EVENT_CODE.enter || code === EVENT_CODE.numpadEnter) {
        if (
          userInput.value === null ||
          userInput.value === '' ||
          isValidValue(parseUserInputToDayjs(displayValue.value))
        ) {
          handleChange()
          pickerVisible.value = false
        }
        event.stopPropagation()
        return
      }

      // if user is typing, do not let picker handle key input
      if (userInput.value) {
        event.stopPropagation()
        return
      }

      if (pickerOptions.value.handleKeydown) {
        pickerOptions.value.handleKeydown(event)
      }
    }

    const onUserInput = (e) => {
      userInput.value = e
      if (!pickerVisible.value) {
        pickerVisible.value = true
      }
    }

    const handleStartInput = (event) => {
      if (userInput.value) {
        userInput.value = [event.target.value, userInput.value[1]]
      } else {
        userInput.value = [event.target.value, null]
      }
    }

    const handleEndInput = (event) => {
      if (userInput.value) {
        userInput.value = [userInput.value[0], event.target.value]
      } else {
        userInput.value = [null, event.target.value]
      }
    }

    const handleStartChange = () => {
      const value = parseUserInputToDayjs(userInput.value && userInput.value[0])
      if (value && value.isValid()) {
        userInput.value = [formatDayjsToString(value), displayValue.value![1]]
        const newValue = [value, parsedValue.value && parsedValue.value[1]]
        if (isValidValue(newValue)) {
          emitInput(newValue)
          userInput.value = null
        }
      }
    }

    const handleEndChange = () => {
      const value = parseUserInputToDayjs(userInput.value && userInput.value[1])
      if (value && value.isValid()) {
        userInput.value = [displayValue.value![0], formatDayjsToString(value)]
        const newValue = [parsedValue.value && parsedValue.value[0], value]
        if (isValidValue(newValue)) {
          emitInput(newValue)
          userInput.value = null
        }
      }
    }

    const pickerOptions = ref<Partial<PickerOptions>>({})
    const onSetPickerOption = <T extends keyof PickerOptions>(
      e: [T, PickerOptions[T]]
    ) => {
      pickerOptions.value[e[0]] = e[1]
      pickerOptions.value.panelReady = true
    }

    const onCalendarChange = (e) => {
      ctx.emit('on-calendar-change', e)
    }

    const onPanelChange = (value, mode, view) => {
      ctx.emit('on-panel-change', value, mode, view)
    }

    provide(TimePickerContextKey, {
      props,
    })

    return {
      // injected popper options
      popperOptions,
      attrs,
      isDatesPicker,
      onBeforeShow,
      onHide,
      handleEndChange,
      handleStartChange,
      handleStartInput,
      handleEndInput,
      onUserInput,
      handleChange,
      handleKeydown,
      popperPaneRef,
      onClickOutside,
      pickerSize,
      isRangeInput,
      onMouseDownInput,
      onMouseLeave,
      onMouseEnter,
      onTouchStartInput,
      onClearIconClick,
      showClose,
      triggerIcon,
      onPick,
      handleFocusInput,
      handleBlurInput,
      pickerVisible,
      pickerActualVisible,
      displayValue,
      parsedValue,
      setSelectionRange,
      refPopper,
      inputRef,
      pickerDisabled,
      onSetPickerOption,
      onCalendarChange,
      onPanelChange,
      focus,
    }
  },
})
</script>
